import DcPage from "./DcPage";

/**
 * Shared renderer for the legal pages: the Terms of Service, the Privacy Policy
 * and the data-deletion instructions.
 *
 * The documents have the same shape — a title, an effective date, then
 * numbered sections of paragraphs and bullet lists, some of whose bullets open
 * with a bold lead-in. One renderer means they cannot drift apart in type,
 * measure or rhythm, which for documents a reader compares side by side is
 * the whole point.
 *
 * Legal prose is set wider and looser than the marketing pages: a 820px measure
 * and 1.75 line height, and body text at .72 rather than the .66 used
 * elsewhere. Long-form reading needs the contrast; a marketing lede does not.
 *
 * A paragraph can carry a link — the Privacy Policy points at the
 * data-deletion page, which gives a mailto: address. The canvas strips the
 * underline from every anchor, so a link set in running text takes
 * `proseLinkStyle`; without it the link reads as text in another colour.
 */

/** A bullet is either plain text or a bold lead-in followed by its clause. */
export type Bullet = string | { lead: string; text: string };

/** A paragraph is plain text, or a node when it has to carry a link. */
export type Block =
  | { p: React.ReactNode }
  | { bullets: Bullet[] };

/**
 * For a link inside a paragraph. The underline is what marks it as a link in
 * running text, where colour alone does not; `:where(.dcv2) a` in styles.ts
 * removes it by default.
 */
export const proseLinkStyle: React.CSSProperties = {
  textDecoration: "underline",
  textUnderlineOffset: 3,
};

export interface LegalSection {
  /** Section number as printed, e.g. "1". Omitted for an unnumbered preamble. */
  n?: string;
  title: string;
  blocks: Block[];
}

interface LegalPageProps {
  eyebrow: string;
  title: string;
  /** Printed verbatim. See the note in Terms.tsx on why this is not computed. */
  effectiveDate: string;
  intro: string;
  sections: LegalSection[];
  /** Optional closing line, e.g. a contact address. */
  footNote?: string;
}

const MEASURE = 820;

const bodyStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 16,
  lineHeight: 1.75,
  color: "rgba(var(--ink-rgb),.72)",
};

function BulletList({ items }: { items: Bullet[] }) {
  return (
    <ul
      style={{
        margin: "14px 0 0",
        padding: 0,
        listStyle: "none",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {items.map((item, i) => {
        const lead = typeof item === "string" ? null : item.lead;
        const text = typeof item === "string" ? item : item.text;
        return (
          <li
            key={i}
            style={{ display: "flex", gap: 14, alignItems: "baseline" }}
          >
            {/* The rotated square used as a list marker across the site. */}
            <span
              aria-hidden="true"
              style={{
                flex: "0 0 auto",
                width: 5,
                height: 5,
                marginTop: 10,
                background: "var(--acc2)",
                transform: "rotate(45deg)",
              }}
            />
            <span style={bodyStyle}>
              {lead && (
                <strong style={{ color: "var(--ink)", fontWeight: 600 }}>
                  {lead}:{" "}
                </strong>
              )}
              {text}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export default function LegalPage({
  eyebrow,
  title,
  effectiveDate,
  intro,
  sections,
  footNote,
}: LegalPageProps) {
  return (
    <DcPage>
      {/* Side gutter on `.site-gutter`, so it narrows on a phone (global.css,
          T101026002). */}
      <section
        className="site-gutter"
        style={{
          paddingTop: 118,
          paddingBottom: 48,
          position: "relative",
          overflow: "clip",
        }}
      >
        <div style={{ maxWidth: MEASURE, margin: "0 auto", position: "relative" }}>
          <div data-rv="">
            <div
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 11.5,
                letterSpacing: ".16em",
                textTransform: "uppercase",
                color: "var(--link)",
                marginBottom: 18,
              }}
            >
              <span className="dc-mark" aria-hidden="true" /> {eyebrow}
            </div>
            <h1
              style={{
                margin: 0,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: "clamp(34px, 4.2vw, 56px)",
                letterSpacing: "-0.025em",
                lineHeight: 1.1,
              }}
            >
              {title}
            </h1>
            <div
              style={{
                marginTop: 18,
                fontFamily: "'Manrope', sans-serif",
                fontSize: 12,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                color: "rgba(var(--ink-rgb),.62)",
              }}
            >
              Effective date: {effectiveDate}
            </div>
            <p style={{ ...bodyStyle, marginTop: 26, fontSize: 17 }}>{intro}</p>
          </div>
        </div>
      </section>

      <section
        className="site-gutter"
        style={{ paddingBottom: 118, position: "relative", overflow: "clip" }}
      >
        {/* overflowWrap: an email address or URL in a clause breaks onto the
            next line instead of running past the clipped page edge on a phone.
            It only acts on a word wider than the line, so wider screens render
            as before (T101026002). */}
        <div style={{ maxWidth: MEASURE, margin: "0 auto", position: "relative", overflowWrap: "anywhere" }}>
          {sections.map((s) => (
            <div
              key={s.title}
              data-rv=""
              style={{
                borderTop: "1px solid rgba(var(--ink-rgb),.2)",
                paddingTop: 30,
                marginTop: 30,
              }}
            >
              <h2
                style={{
                  margin: "0 0 14px",
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 600,
                  fontSize: 22,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.3,
                  display: "flex",
                  gap: 14,
                }}
              >
                {s.n && (
                  <span
                    style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontSize: 15,
                      fontWeight: 500,
                      color: "var(--link)",
                      flex: "0 0 auto",
                      lineHeight: 1.75,
                    }}
                  >
                    {s.n}
                  </span>
                )}
                <span>{s.title}</span>
              </h2>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                  // Indented to the heading's text, past the numeral.
                  paddingLeft: s.n ? 29 : 0,
                }}
              >
                {s.blocks.map((b, i) =>
                  "p" in b ? (
                    <p key={i} style={bodyStyle}>
                      {b.p}
                    </p>
                  ) : (
                    <BulletList key={i} items={b.bullets} />
                  ),
                )}
              </div>
            </div>
          ))}

          {footNote && (
            <p
              data-rv=""
              style={{
                ...bodyStyle,
                marginTop: 40,
                paddingTop: 30,
                borderTop: "1px solid rgba(var(--ink-rgb),.2)",
                fontSize: 15,
                color: "rgba(var(--ink-rgb),.62)",
              }}
            >
              {footNote}
            </p>
          )}
        </div>
      </section>
    </DcPage>
  );
}
